import type { Locale } from "@/i18n/translations";

type ChecklistPageCopy = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heading: string;
  intro: string;
  dateLabel: string;
  dateHint: string;
  generate: string;
  reset: string;
  print: string;
  emptyTitle: string;
  emptyBody: string;
  pastDate: string;
  progress: (done: number, total: number) => string;
  startNow: string;
  startNowSummary: string;
  dateColumn: string;
  savedNote: string;
  ctaHeading: string;
  ctaBody: string;
  ctaButton: string;
  guidesHeading: string;
  howItWorksHeading: string;
  howItWorks: string[];
  accuracyHeading: string;
  accuracyBody: string;
};

type GuidesPageCopy = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heading: string;
  intro: string;
  readMore: string;
  ctaHeading: string;
  ctaBody: string;
  ctaButton: string;
  backToGuides: string;
  updatedLabel: string;
  breadcrumbHome: string;
};

export const checklistCopy: Record<Locale, ChecklistPageCopy> = {
  en: {
    metaTitle: "Wedding planning checklist by date — free and printable | Nellove",
    metaDescription:
      "Enter your wedding date and get a month-by-month wedding planning checklist counting back from the day. Free, printable, no account needed.",
    eyebrow: "Free tool",
    heading: "Your wedding checklist, counted back from your date",
    intro:
      "Enter your wedding date and this page builds a month-by-month plan working backwards from it. Tick things off as you go — your progress stays on this device. No account, no email required.",
    dateLabel: "Wedding date",
    dateHint: "Pick the date you are planning for. You can change it at any time.",
    generate: "Build my checklist",
    reset: "Clear progress",
    print: "Print or save as PDF",
    emptyTitle: "Pick a date to begin",
    emptyBody:
      "The checklist adapts to how much time you actually have. A date eighteen months away and a date three months away produce very different plans.",
    pastDate: "That date has already passed. Choose a future date to build a plan.",
    progress: (done, total) => `${done} of ${total} done`,
    startNow: "Start now",
    startNowSummary:
      "Your date is close, so these phases are already behind you. Everything below is what to do first.",
    dateColumn: "Aim for",
    savedNote: "Your ticks are saved in this browser only. Nothing is sent to us.",
    ctaHeading: "Keep this list live instead of on paper",
    ctaBody:
      "Nellove keeps this checklist tied to your date, your guest list and RSVPs, your seating chart, your budget and guest messaging by SMS or WhatsApp in one place. Free forever — request an early-access invitation.",
    ctaButton: "Join free",
    guidesHeading: "Read next",
    howItWorksHeading: "How this checklist is built",
    howItWorks: [
      "Each phase is placed by counting backwards from your wedding date, so the dates you see are yours, not generic advice.",
      "If your date is close, the phases that have already passed are merged into one “Start now” list rather than shown as missed.",
      "Tasks are the ones that apply to most weddings. Skip what does not apply to yours — nothing here is compulsory.",
    ],
    accuracyHeading: "A note on accuracy",
    accuracyBody:
      "Legal requirements, waiting periods and paperwork differ by country, province and state, so the checklist tells you to verify them rather than guessing on your behalf. We do not include cost estimates, because a number that is wrong for your region is worse than no number at all.",
  },
  fr: {
    metaTitle: "Checklist mariage selon votre date — gratuite et imprimable | Nellove",
    metaDescription:
      "Indiquez votre date de mariage et obtenez un rétroplanning mois par mois, calculé à partir du jour J. Gratuit, imprimable, sans compte.",
    eyebrow: "Outil gratuit",
    heading: "Votre checklist mariage, calculée à partir de votre date",
    intro:
      "Indiquez votre date de mariage et cette page construit un plan mois par mois en remontant le temps. Cochez au fur et à mesure — votre progression reste sur cet appareil. Sans compte, sans courriel.",
    dateLabel: "Date du mariage",
    dateHint: "Choisissez la date que vous préparez. Vous pouvez la modifier à tout moment.",
    generate: "Créer ma checklist",
    reset: "Effacer la progression",
    print: "Imprimer ou enregistrer en PDF",
    emptyTitle: "Choisissez une date pour commencer",
    emptyBody:
      "La checklist s'adapte au temps dont vous disposez réellement. Une date dans dix-huit mois et une date dans trois mois donnent deux plans très différents.",
    pastDate: "Cette date est déjà passée. Choisissez une date future pour créer un plan.",
    progress: (done, total) => `${done} sur ${total} fait`,
    startNow: "À faire dès maintenant",
    startNowSummary:
      "Votre date approche : ces étapes sont déjà derrière vous. Tout ce qui suit est à traiter en premier.",
    dateColumn: "Viser",
    savedNote: "Vos cases cochées sont enregistrées dans ce navigateur uniquement. Rien ne nous est envoyé.",
    ctaHeading: "Gardez cette liste vivante plutôt que sur papier",
    ctaBody:
      "Nellove réunit cette checklist liée à votre date, votre liste d'invités et vos RSVP, votre plan de table, votre budget et vos messages aux invités par SMS ou WhatsApp au même endroit. Gratuit pour toujours — demandez une invitation d’accès anticipé.",
    ctaButton: "Rejoindre gratuitement",
    guidesHeading: "À lire ensuite",
    howItWorksHeading: "Comment cette checklist est construite",
    howItWorks: [
      "Chaque étape est placée en remontant à partir de votre date : les échéances affichées sont les vôtres, pas des conseils génériques.",
      "Si votre date est proche, les étapes déjà passées sont regroupées dans une liste « À faire dès maintenant » plutôt que présentées comme manquées.",
      "Les tâches sont celles qui s'appliquent à la plupart des mariages. Ignorez ce qui ne concerne pas le vôtre : rien n'est obligatoire ici.",
    ],
    accuracyHeading: "Une note sur l'exactitude",
    accuracyBody:
      "Les démarches légales et les délais varient selon le pays et la province : la checklist vous invite à les vérifier plutôt que de deviner à votre place. Nous n'affichons pas d'estimations de coûts, car un montant faux pour votre région est pire que pas de montant du tout.",
  },
};

export const guidesCopy: Record<Locale, GuidesPageCopy> = {
  en: {
    metaTitle: "Wedding planning guides | Nellove",
    metaDescription:
      "Practical wedding planning guides from Nellove: a month-by-month timeline, guest list and RSVP without a spreadsheet, and how to share the planning with your partner.",
    eyebrow: "Guides",
    heading: "Wedding planning, explained plainly",
    intro:
      "Short, practical guides written around how weddings are actually organised. No trends, no invented statistics — just the order of decisions and the parts that catch people out.",
    readMore: "Read the guide",
    ctaHeading: "Plan it in one place",
    ctaBody:
      "Nellove brings guests and RSVP, a seating chart, a checklist tied to your date, a shared budget and guest messaging by SMS or WhatsApp into one place. Free forever — request an early-access invitation.",
    ctaButton: "Join free",
    backToGuides: "All guides",
    updatedLabel: "Updated",
    breadcrumbHome: "Home",
  },
  fr: {
    metaTitle: "Guides d'organisation de mariage | Nellove",
    metaDescription:
      "Des guides concrets pour organiser un mariage : rétroplanning mois par mois, liste d'invités et RSVP sans tableur, et comment partager l'organisation à deux.",
    eyebrow: "Guides",
    heading: "L'organisation d'un mariage, expliquée simplement",
    intro:
      "Des guides courts et concrets, écrits à partir de la façon dont les mariages s'organisent vraiment. Pas de tendances, pas de statistiques inventées : l'ordre des décisions et les pièges habituels.",
    readMore: "Lire le guide",
    ctaHeading: "Tout au même endroit",
    ctaBody:
      "Nellove réunit invités et RSVP, plan de table, checklist liée à votre date, budget partagé et messages aux invités par SMS ou WhatsApp. Gratuit pour toujours — demandez une invitation d’accès anticipé.",
    ctaButton: "Rejoindre gratuitement",
    backToGuides: "Tous les guides",
    updatedLabel: "Mis à jour le",
    breadcrumbHome: "Accueil",
  },
};
